import React, { useEffect, useRef, useState, useCallback } from "react";
import { Calendar, MapPin, Building2, FileText, User } from "lucide-react";
import { motion, useMotionValue } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { COLOR } from "./constants/colors";
import AuthModal from "./components/modals/AuthModal";
import ConfirmationModal from "./components/modals/ConfirmationModal";
import SuccessModal from "./components/modals/SuccessModal";
import Header from "./components/Header";
import {
  BasicButton,
  PrimaryButton,
  TransparentButton,
  LinkButton,
} from "./components/Buttons";
import TimeSlot from "./components/TimeSlot";

interface Slot {
  time: string;
  booked: number;
  capacity: number;
}

interface Day {
  id: string;
  dateLabel: string;
  slots: Slot[];
}

interface Seller {
  name: string;
  linkedInUrl?: string;
  yearsInResidence: number;
  profilePicture?: string;
}

const SELLER_INFO: Seller = {
  name: "Daniel Hariyanto",
  linkedInUrl: "https://www.linkedin.com/in/danielhariyanto/",
  yearsInResidence: 1.5,
  profilePicture:
    "https://media.licdn.com/dms/image/v2/D5603AQFF7x6zU2RLdw/profile-displayphoto-shrink_800_800/B56ZO3qSAeHoAc-/0/1733953135239?e=1764806400&v=beta&t=iklP59SBwOhM3SGpFZ1FcDpd4ktqkZbZmCOR_R_xfWU",
};

const DAYS: Day[] = [
  {
    id: "2025-11-23",
    dateLabel: "Sunday, Nov 23",
    slots: [
      { time: "3:00 PM", booked: 5, capacity: 8 },  // TODO: hook up to backend
      { time: "4:00 PM", booked: 8, capacity: 8 },
      { time: "5:00 PM", booked: 5, capacity: 8 },
    ],
  },
];

// TODO: hook up to backend
const IMAGES: string[] = [
  "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?q=80&w=2000&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1524758631624-e2822e304c36?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1493666438817-866a91353ca9?q=80&w=1600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1502673530728-f79b4cab31b1?q=80&w=1600&auto=format&fit=crop",
];

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

export default function Page() {
  const navigate = useNavigate();
  type Selected = { day: string; time: string } | null;
  const [selected, setSelected] = useState<Selected>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [hasLinkedIn, setHasLinkedIn] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const planRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  // restore scroll refs used by the onScroll effect
  const baseYRef = useRef<number | null>(null);
  const ticking = useRef(false);
  const progressMV = useMotionValue(0);

  // mobile carousel touch/swipe helpers
  const startXRef = useRef<number | null>(null);
  const deltaXRef = useRef<number>(0);

  const setTrackTransformPx = (x: number, animate = true) => {
    if (!trackRef.current) return;
    trackRef.current.style.transition = animate
      ? "transform 300ms ease"
      : "none";
    trackRef.current.style.transform = `translateX(${x}px)`;
  };

  // snap to currentIndex when it changes and handle window resize
  useEffect(() => {
    const updatePosition = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      setTrackTransformPx(-currentIndex * w, false);
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [currentIndex]);

  const onTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    deltaXRef.current = 0;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startXRef.current === null) return;
    const curX = e.touches[0].clientX;
    deltaXRef.current = curX - startXRef.current;
    const w = window.innerWidth || 0;
    const offset = -currentIndex * w + deltaXRef.current;
    setTrackTransformPx(offset, false);
  };

  const onTouchEnd = () => {
    if (startXRef.current === null) return;
    const threshold = Math.min(80, (window.innerWidth || 0) * 0.12);
    const dx = deltaXRef.current;
    startXRef.current = null;
    deltaXRef.current = 0;
    if (dx < -threshold && currentIndex < IMAGES.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (dx > threshold && currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else {
      const w = window.innerWidth || 0;
      setTrackTransformPx(-currentIndex * w, true);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      if (!planRef.current) return;
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const top = planRef.current!.getBoundingClientRect().top;
        if (top > 16) {
          baseYRef.current = null;
          progressMV.set(0);
          ticking.current = false;
          return;
        }
        if (baseYRef.current === null) baseYRef.current = window.scrollY;
        const delta = Math.max(
          0,
          window.scrollY - (baseYRef.current ?? window.scrollY)
        );
        const p = clamp01(delta / (0.25 * window.innerHeight));
        progressMV.set(p);
        ticking.current = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [progressMV]);

  const handleContinue = useCallback(async () => {
    if (!selected) {
      setValidationError(true);
      setTimeout(() => setValidationError(false), 2000);
      return;
    }

    setConfirmationOpen(true);
  }, [selected]);

  const handleSignIn = useCallback((provider: string) => {
    // Simulate sign-in
    setIsSignedIn(true);
    if (provider === "linkedin") {
      setHasLinkedIn(true);
    }
    setAuthOpen(false);
  }, []);

  const handleSignInClick = useCallback(() => {
    setAuthOpen(true);
  }, []);

  const handleConfirmVisit = useCallback(() => {
    setConfirmationOpen(false);
    setSuccessOpen(true);
  }, []);

  const handleSuccessClose = useCallback(() => {
    setSuccessOpen(false);
    // Navigate to other neighborhood finds
    // window.location.href = '/neighborhood-finds';
  }, []);

  const description =
    "We're moving and everything must go! Modern, gently-used furniture from a smoke-free, pet-free home: a plush sectional, queen platform bed, media console, dining set, plus décor and kitchenware. Prices are clearly tagged and negotiable for multi-item bundles. Elevator building with easy load-out — bring a friend if you plan to carry larger pieces. Card and cash accepted.";

  const selectedDay = DAYS.find((d) => d.id === selected?.day);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: COLOR.bg,
        color: COLOR.text,
        fontFamily: "'Nunito', ui-sans-serif, system-ui",
      }}
    >
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSignIn={handleSignIn}
      />
      <ConfirmationModal
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmVisit}
        onSignInClick={handleSignInClick}
        selection={
          selected && selectedDay
            ? { ...selected, dateLabel: selectedDay.dateLabel }
            : null
        }
        isSignedIn={isSignedIn}
      />
      <SuccessModal
        open={successOpen}
        onClose={handleSuccessClose}
        selection={
          selected && selectedDay
            ? { ...selected, dateLabel: selectedDay.dateLabel }
            : null
        }
      />

      {/* overlay: full gallery */}
      {galleryOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
          onClick={() => setGalleryOpen(false)}
        >
          <div
            className="w-full max-w-6xl max-h-[90vh] rounded-2xl bg-white overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 pb-3 flex-shrink-0">
              <h3
                className="text-lg font-extrabold"
                style={{ color: COLOR.text }}
              >
                All photos
              </h3>
              <BasicButton
                onClick={() => setGalleryOpen(false)}
                className="text-sm font-semibold px-3 py-1 rounded-md"
              >
                Close
              </BasicButton>
            </div>
            <div className="overflow-auto p-4 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {IMAGES.map((src: string, i: number) => (
                  <div
                    key={i}
                    className="w-full rounded-lg overflow-hidden"
                    style={{ background: COLOR.border }}
                  >
                    <img
                      src={src}
                      alt={`Photo ${i + 1}`}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Header variant="listing" />

      <main className="mx-auto max-w-4xl mt-6 px-4 sm:px-6 pb-6">
        <section>
          <div
            className="relative overflow-hidden rounded-3xl border bg-white shadow-sm"
            style={{ borderColor: COLOR.border }}
          >
            {/* DESKTOP: collage up to 4 photos */}
            <div className="hidden sm:grid grid-cols-2 grid-rows-2 gap-1 h-[300px] sm:h-[420px]">
              {IMAGES.slice(0, 4).map((src: string, idx: number) => {
                const isLastWithMore = idx === 3 && IMAGES.length > 4;
                return (
                  <div key={idx} className="relative overflow-hidden">
                    <img
                      src={src}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {isLastWithMore && (
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.35)" }}
                      >
                        <span className="text-white text-2xl font-extrabold">
                          +{IMAGES.length - 4}
                        </span>
                        <BasicButton
                          onClick={() => setGalleryOpen(true)}
                          className="mt-3 rounded-md px-3 py-2 text-sm font-semibold"
                        >
                          Show all photos
                        </BasicButton>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* MOBILE: swipe carousel — one full-width slide per image */}
            <div className="sm:hidden">
              <div className="relative overflow-hidden h-[220px]">
                {/* track: we control transform directly for swipe */}
                <div
                  ref={trackRef}
                  role="region"
                  aria-label="Photos carousel"
                  className="flex h-full"
                  style={{ width: `${IMAGES.length * 100}vw` }}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {IMAGES.map((src: string, i: number) => (
                    <div
                      key={i}
                      className="flex-shrink-0 h-full relative"
                      style={{ width: '100vw' }}
                    >
                      <img
                        src={src}
                        alt={`Slide ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* first slide shows count + show all */}
                      {i === 0 && (
                        <div
                          className="absolute left-3 bottom-3 inline-flex items-center gap-2 px-3 py-1 rounded-full"
                          style={{
                            background: "rgba(255,255,255,0.9)",
                            border: `1px solid ${COLOR.border}`,
                          }}
                        >
                          <span
                            className="text-sm font-extrabold"
                            style={{ color: COLOR.text }}
                          >
                            {IMAGES.length} photos
                          </span>
                          <LinkButton
                            onClick={() => setGalleryOpen(true)}
                            className="text-xs font-semibold"
                            style={{
                              color: COLOR.main,
                              background: "transparent",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            Show all
                          </LinkButton>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* dots indicator centered bottom */}
                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-3 flex items-center gap-2">
                  {IMAGES.map((_: string, i: number) => (
                    <button
                      key={i}
                      aria-label={`Go to photo ${i + 1}`}
                      onClick={() => setCurrentIndex(i)}
                      className={`w-2 h-2 rounded-full ${
                        i === currentIndex ? "bg-white" : "bg-white/40"
                      }`}
                      style={{
                        border: `1px solid ${
                          i === currentIndex ? COLOR.border : "transparent"
                        }`,
                      }}
                    />
                  ))}
                </div>

                {/* info icon bottom-right: shows current / total */}
                <div
                  className="absolute right-3 bottom-3 inline-flex items-center gap-2 px-2 py-1 rounded-full bg-white/90"
                  style={{ border: `1px solid ${COLOR.border}` }}
                >
                  <span
                    style={{ color: COLOR.text }}
                    className="text-xs font-semibold"
                  >
                    {currentIndex + 1} / {IMAGES.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Title & Tags */}
          <div className="mt-6 space-y-4">
            <h1
              className="text-3xl font-extrabold leading-snug"
              style={{ color: COLOR.text }}
            >
              All must go — discounts on all items and furniture!
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  backgroundColor: COLOR.bg,
                  border: `1px solid ${COLOR.main}`,
                  color: COLOR.text,
                }}
              >
                <MapPin className="h-3.5 w-3.5" style={{ color: COLOR.main }} />{" "}
                Belltown, Seattle, WA
              </span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  backgroundColor: COLOR.bg,
                  border: `1px solid ${COLOR.main}`,
                  color: COLOR.text,
                }}
              >
                <Building2
                  className="h-3.5 w-3.5"
                  style={{ color: COLOR.main }}
                />{" "}
                Apartment
              </span>
            </div>
          </div>

          {/* Plan Your Visit */}
          <motion.div
            id="plan-your-visit"
            ref={planRef}
            role="region"
            aria-label="Plan your visit"
            className="mt-6 sticky z-20 rounded-3xl border bg-white shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/60"
            style={{
              borderColor: COLOR.border,
              padding: 0,
              maxHeight: 320,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              top: "calc(4rem + 0.5rem)", // Header height (py-4 = 2rem top + 2rem bottom) + 0.5rem gap
            }}
          >
            {/* Sticky header inside card */}
            <div
              className="flex items-center justify-between gap-4 px-4 py-3 bg-white bg-white"
              style={{
                position: "sticky",
                top: 0,
                borderBottom: `1px solid ${COLOR.border}`,
                zIndex: 1,
              }}
            >
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" style={{ color: COLOR.main }} />
                <h2
                  className="text-lg font-extrabold"
                  style={{ color: COLOR.text }}
                >
                  Plan your visit
                </h2>
              </div>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!selected}
                selected={!!selected}
                validationError={validationError}
                className="rounded-xl px-4 py-2 text-sm font-extrabold transition disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              >
                Continue
              </PrimaryButton>
            </div>

            {/* Scrollable contents below sticky header */}
            <div
              className="space-y-4 px-4 py-4 bg-white"
              style={{
                overflowY: "auto",
                maxHeight: "calc(320px - 56px)", // 56px = header height (adjust if needed)
                // Optionally: scrollbar styling
              }}
            >
              {DAYS.map((d) => (
                <div key={d.id} className="space-y-2">
                  <p
                    className="text-sm font-extrabold"
                    style={{ color: COLOR.text }}
                  >
                    {d.dateLabel}
                  </p>
                  <div className="flex gap-2 overflow-x-auto snap-x">
                    {d.slots.map((s) => {
                      const isSelected =
                        selected?.day === d.id && selected?.time === s.time;
                      const onSelect = () => {
                        setSelected({ day: d.id, time: s.time });

                        // Track time slot selection
                        if (process.env.REACT_APP_ENABLE_ANALYTICS === "true") {
                          if (
                            typeof window !== "undefined" &&
                            (window as any).gtag
                          ) {
                            (window as any).gtag(
                              "event",
                              "time_slot_selected",
                              {
                                event_category: "engagement",
                                event_label: `${d.dateLabel} ${s.time}`,
                              }
                            );
                          }
                        }
                      };
                      return (
                        <TimeSlot
                          key={`${d.id}-${s.time}`}
                          slot={{ ...s, day: d.id }}
                          selected={!!isSelected}
                          onSelect={onSelect}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Description */}
          <div
            className="mt-6 rounded-3xl border bg-white p-4"
            style={{ borderColor: COLOR.border }}
          >
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: COLOR.main }} />
              <h3
                className="text-lg font-extrabold"
                style={{ color: COLOR.text }}
              >
                Description
              </h3>
            </div>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: COLOR.text }}
            >
              {descExpanded
                ? description
                : description.slice(0, 180) +
                  (description.length > 180 ? "…" : "")}
            </p>
            {description.length > 180 && (
              <LinkButton
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-3 text-sm font-semibold underline hover:opacity-70 transition"
                style={{
                  color: COLOR.main,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                }}
              >
                {descExpanded ? "See less" : "See more"}
              </LinkButton>
            )}
          </div>

          {/* Location & Seller Information */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div
              className="rounded-3xl border bg-white p-4 flex flex-col h-full"
              style={{ borderColor: COLOR.border }}
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: COLOR.main }} />
                <h3
                  className="text-lg font-extrabold"
                  style={{ color: COLOR.text }}
                >
                  Location
                </h3>
              </div>
              <div
                className="mt-3 mb-3 relative flex-1 w-full overflow-hidden rounded-2xl"
                style={{
                  border: `1px solid ${COLOR.border}`,
                  background: COLOR.bg,
                }}
              >
                <iframe
                  title="Approximate Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Belltown,Seattle,WA&zoom=14&maptype=roadmap"
                ></iframe>
              </div>
              <p
                className="text-xs leading-relaxed"
                style={{ color: COLOR.subtext }}
              >
                Approximate area only. Exact address shared after confirmation.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <div
                className="rounded-3xl border bg-white p-4 flex-1"
                style={{ borderColor: COLOR.border }}
              >
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5" style={{ color: COLOR.main }} />
                  <h3
                    className="text-lg font-extrabold"
                    style={{ color: COLOR.text }}
                  >
                    Seller Information
                  </h3>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div
                    className="flex-shrink-0 rounded-full overflow-hidden"
                    style={{
                      width: 56,
                      height: 56,
                      backgroundColor: COLOR.border,
                      border: `2px solid ${COLOR.border}`,
                    }}
                  >
                    {SELLER_INFO.profilePicture ? (
                      <img
                        src={SELLER_INFO.profilePicture}
                        alt={`${SELLER_INFO.name}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ color: COLOR.subtext }}
                      >
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-sm font-extrabold"
                      style={{ color: COLOR.text }}
                    >
                      {SELLER_INFO.name}
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: COLOR.subtext }}
                    >
                      {SELLER_INFO.yearsInResidence}{" "}
                      {SELLER_INFO.yearsInResidence === 1 ? "year" : "years"} in
                      current residence
                    </p>
                    {SELLER_INFO.linkedInUrl ? (
                      <a
                        href={SELLER_INFO.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs font-semibold hover:opacity-70 transition"
                        style={{ color: COLOR.main }}
                      >
                        View LinkedIn →
                      </a>
                    ) : (
                      <p
                        className="text-xs mt-2"
                        style={{ color: COLOR.subtext }}
                      >
                        LinkedIn not connected
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="rounded-3xl border bg-white p-5 sm:p-6 flex flex-col"
                style={{ borderColor: COLOR.border }}
              >
                <h3
                  className="text-lg font-extrabold"
                  style={{ color: COLOR.text }}
                >
                  Interested in selling too?
                </h3>
                <p
                  className="mt-2 text-xs leading-relaxed flex-1"
                  style={{ color: COLOR.subtext }}
                >
                  Tili helps you clear space and earn cash — hassle-free.
                </p>
                <PrimaryButton
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate("/");
                  }}
                  className="mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-extrabold transition hover:opacity-90"
                >
                  Join waitlist
                </PrimaryButton>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
